import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispute } from '../../context/DisputeContext';
import { api } from '../../utils/api';

const FeedbackPage = () => {
  const navigate = useNavigate();
  const { dispute } = useDispute();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);

  useEffect(() => {
    if (!dispute || !dispute.id || !dispute.partyAId) {
      navigate('/start');
      return;
    }

    fetchFeedback();
  }, [dispute]);

  const fetchFeedback = async () => {
    if (!dispute?.id || !dispute?.partyAId) return;

    try {
      setLoading(true);
      const data = await api.getFeedback(dispute.id, dispute.partyAId);
      setFeedbackData(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleReviseAnswers = () => {
    navigate('/assessment');
  };

  const handleBackToStatus = () => {
    navigate('/status');
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="card">
          <div className="text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Error Loading Feedback</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button onClick={handleBackToStatus} className="btn-primary">
              Back to Status
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!feedbackData?.hasFeedback) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="card">
          <div className="text-center">
            <div className="text-4xl mb-4">ℹ️</div>
            <h2 className="text-xl font-bold mb-2">No Feedback Available</h2>
            <p className="text-gray-600 mb-4">
              You don't have any attorney feedback at this time.
            </p>
            <button onClick={handleBackToStatus} className="btn-primary">
              Back to Status
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { feedback, scoring, reviewStatus } = feedbackData;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={handleBackToStatus}
          className="text-primary-500 hover:text-primary-600 mb-4 flex items-center"
        >
          <span className="mr-2">&larr;</span> Back to Status
        </button>
        <h1 className="text-3xl font-bold mb-3">Attorney Feedback</h1>
        <p className="text-gray-600">
          An attorney has reviewed your submission and provided feedback for improvements.
        </p>
      </div>

      {/* Review Status */}
      <div className="card mb-6 bg-yellow-50 border border-yellow-300">
        <div className="flex items-start">
          <div className="text-3xl mr-4">⚖️</div>
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-2 text-yellow-800">
              Action Required: Revise Your Submission
            </h2>
            <p className="text-gray-700 mb-3">
              Please review the feedback below and update your responses accordingly.
              Once you've made the necessary improvements, resubmit your assessment.
            </p>
            <div className="inline-block px-3 py-1 bg-yellow-100 rounded-full text-sm font-medium text-yellow-800 capitalize">
              Status: {reviewStatus?.replace(/_/g, ' ') || 'Modifications Requested'}
            </div>
          </div>
        </div>
      </div>

      {/* Assessment Score */}
      {scoring && scoring.score !== undefined && (
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Assessment Score</h2>
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-gray-800 mb-1">Your Score</h3>
                <p className="text-xs text-gray-600">Automated quality assessment</p>
              </div>
              <div className="text-right">
                <div className={`text-5xl font-bold ${
                  scoring.score >= 70 ? 'text-green-600' :
                  scoring.score >= 50 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {scoring.score}
                </div>
                <p className="text-sm text-gray-600">out of 100</p>
              </div>
            </div>

            {scoring.recommendation && (
              <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Recommendation:</span>{' '}
                  {scoring.recommendation.replace(/_/g, ' ')}
                </p>
              </div>
            )}
          </div>

          {/* Red Flags */}
          {scoring.redFlags && scoring.redFlags.length > 0 && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-bold text-red-800 mb-2 flex items-center">
                <span className="text-xl mr-2">⚠</span>
                Issues Detected ({scoring.redFlags.length})
              </h4>
              <ul className="space-y-2">
                {scoring.redFlags.map((flag, idx) => (
                  <li key={idx} className="text-sm text-red-700 flex items-start">
                    <span className="mr-2">•</span>
                    <div>
                      <span className="font-medium capitalize">{flag.type?.replace(/_/g, ' ')}:</span>{' '}
                      {flag.description}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Attorney Feedback */}
      <div className="card mb-6">
        <h2 className="text-xl font-bold mb-4">Attorney's Feedback</h2>

        {/* General Message */}
        {feedback.message && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-gray-800">{feedback.message}</p>
          </div>
        )}

        {/* Questions to Improve */}
        {feedback.questionsToImprove && feedback.questionsToImprove.length > 0 && (
          <div className="mt-6">
            <h3 className="font-bold mb-3">Questions That Need Improvement ({feedback.questionsToImprove.length})</h3>
            <div className="space-y-4">
              {feedback.questionsToImprove.map((question, index) => (
                <div key={index} className="border border-yellow-300 bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-start mb-2">
                    <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded text-xs font-bold mr-3">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 mb-1">
                        Question ID: {question.questionId}
                      </p>
                      {question.issue && (
                        <div className="mt-2 p-2 bg-white rounded border border-yellow-200">
                          <p className="text-xs font-medium text-gray-600 mb-1">Issue:</p>
                          <p className="text-sm text-gray-800">{question.issue}</p>
                        </div>
                      )}
                      {question.suggestion && (
                        <div className="mt-2 p-2 bg-white rounded border border-yellow-200">
                          <p className="text-xs font-medium text-gray-600 mb-1">Suggestion:</p>
                          <p className="text-sm text-gray-800">{question.suggestion}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Feedback Timestamp */}
        {feedback.providedAt && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Feedback provided on {new Date(feedback.providedAt).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="card bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-200">
        <h3 className="font-bold mb-4">Next Steps</h3>
        <p className="text-sm text-gray-700 mb-4">
          Please revise your assessment responses based on the attorney's feedback.
          You can edit your answers and resubmit for review.
        </p>
        <div className="flex gap-4">
          <button
            onClick={handleReviseAnswers}
            className="btn-primary flex-1"
          >
            Revise My Answers
          </button>
          <button
            onClick={handleBackToStatus}
            className="btn-secondary flex-1"
          >
            Back to Status
          </button>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-start">
          <div className="text-2xl mr-3">💡</div>
          <div className="text-sm">
            <p className="font-medium text-gray-900 mb-1">Need Help?</p>
            <p className="text-gray-600">
              If you have questions about the feedback or need clarification, contact our support team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
